import {
  Carriers,
  DhlEcsServices,
} from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import { CarrierAndService } from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import {
  DEFAULT_DYNAMIC_RATE_PRICING_RULES,
  DynamicRatePricingRule,
  DynamicRatePricingSettings,
  DynamicRatePricingSettingsSchema,
} from "@redotech/redo-fulfillment-carrier-common/dynamic-rate-pricing";
import { groupInput, input, InputSchema, listInput } from "@redotech/ui/form";

const dynamicRatePricingRuleForm = groupInput({
  source: input<CarrierAndService>(),
  benchmarks: input<CarrierAndService[]>(),
  minBeatRateBy: input<number>(),
  maxBeatRateBy: input<number>(),
});

export type DynamicRatePricingRuleValue = InputSchema.Value<
  typeof dynamicRatePricingRuleForm
>;

function ruleFormValue(
  rule: DynamicRatePricingRule,
): DynamicRatePricingRuleValue {
  return {
    source: { carrier: rule.carrier, service: rule.service },
    benchmarks: rule.benchmarks,
    minBeatRateBy: rule.minBeatRateBy,
    maxBeatRateBy: rule.maxBeatRateBy,
  };
}

function savedRule(value: DynamicRatePricingRuleValue) {
  return {
    ...value.source,
    benchmarks: value.benchmarks,
    minBeatRateBy: Number(value.minBeatRateBy.toFixed(2)),
    maxBeatRateBy: Number(value.maxBeatRateBy.toFixed(2)),
  };
}

const ruleDefault: DynamicRatePricingRuleValue = {
  source: {
    carrier: Carriers.DHL_ECS,
    service: DhlEcsServices.DhlParcelGround,
  },
  benchmarks: [],
  minBeatRateBy: 0.01,
  maxBeatRateBy: 0.3,
};

export const dynamicRatePricingSettingsForm = groupInput(
  {
    enabled: input<boolean>(),
    rules: listInput(
      () => dynamicRatePricingRuleForm,
      () => ruleDefault,
      (_, index) => index,
    ),
  },
  {
    validator: (value) => {
      const result = DynamicRatePricingSettingsSchema.safeParse({
        enabled: value.enabled,
        rules: value.rules.map((rule) => ({
          ...rule.source,
          benchmarks: rule.benchmarks,
          minBeatRateBy: rule.minBeatRateBy,
          maxBeatRateBy: rule.maxBeatRateBy,
        })),
      });
      return result.success
        ? []
        : result.error.issues.map((issue) => {
            const ruleIndex = issue.path[1];
            return typeof ruleIndex === "number"
              ? `${value.rules[ruleIndex]?.source.service ?? "Service"}: ${issue.message}`
              : issue.message;
          });
    },
  },
);

export type DynamicRatePricingSettingsValue = InputSchema.Value<
  typeof dynamicRatePricingSettingsForm
>;

export function createDynamicRatePricingSettingsFormValue(
  settings: DynamicRatePricingSettings | undefined,
): DynamicRatePricingSettingsValue {
  return {
    enabled: settings?.enabled ?? false,
    rules: (settings?.rules ?? DEFAULT_DYNAMIC_RATE_PRICING_RULES).map(
      ruleFormValue,
    ),
  };
}

export const dynamicRatePricingSettingsDefault =
  createDynamicRatePricingSettingsFormValue(undefined);

export function getUpdatedDynamicRatePricingSettings(
  value: DynamicRatePricingSettingsValue,
  existing: DynamicRatePricingSettings | undefined,
): DynamicRatePricingSettings | undefined {
  if (!value.enabled && !existing) {
    return undefined;
  }

  return DynamicRatePricingSettingsSchema.parse({
    enabled: value.enabled,
    rules: value.rules.map(savedRule),
  });
}
